import React, { ReactElement, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RecursiveLinksType } from 'b2b-components/theme/components/Header/ModalMenu';
import { TopBarGroupType } from 'b2b-components/theme/components/Header/TopBar';
import { getProfile } from 'b2b-apps/core/context/redux/selectors';
import { useActiveLanguage } from 'b2b-apps/core/i18n/hooks/useActiveLanguage';
import { useAppAssortment } from 'b2b-apps/shop/app/hooks';
import { ENTRY_ID_TYPE, useEntry } from 'b2b-apps/shop/content/hooks/useEntry';
import { useCheckPermissions, useLogoutAction, useUserData } from 'b2b-apps/shop/context/hooks';
import { PERMISSION } from 'b2b-apps/shop/context/utils/permissions';
import { useChangeLanguageAction, useTranslate } from 'b2b-apps/shop/i18n/hooks';
import { getAvailableLanguages, LANGUAGE_LABELS } from 'b2b-apps/shop/i18n/utils/language';
import { ROUTES } from 'b2b-apps/shop/routes/components/Router';
import { useRouterPush, useUrlCreator } from 'b2b-apps/shop/routes/hooks';
import { openLink } from 'b2b-apps/shop/routes/utils/url';
import { LANGUAGE_CODE } from 'b2b-common/core/i18n/consts';
import { EnhancedComponentProps, EnhancedComponentType, InjectedProps, WrappedComponentType } from './';

export const withContainer = (Component: WrappedComponentType): EnhancedComponentType => {
  const WithContainer = (props: EnhancedComponentProps): ReactElement => {
    const { onLinkClick, catalogLinks, catalog, ownAssortment } = useAppAssortment();
    const t = useTranslate();
    const activeLanguage = useActiveLanguage();
    const { isLoggedIn } = useUserData();
    const push = useRouterPush();
    const content = useEntry(ENTRY_ID_TYPE.header);
    const profile = useSelector(getProfile);
    const changeLanguage = useChangeLanguageAction();
    const hasMyAccountPermissions = useCheckPermissions([
      PERMISSION.accessMyAccountDashboard,
    ]);
    const hasAccessWishlistPermissions = useCheckPermissions([
      PERMISSION.accessWishlist,
    ]);
    const urlCreator = useUrlCreator();
    const redirectToLogout = useLogoutAction();

    const contentLinks = useMemo(
      (): RecursiveLinksType[] => {
        if (
          !content.isLoaded
          || !content.data
          || !Array.isArray(content.data.group)
        ) {
          return [];
        }

        return content.data.group.map((group: TopBarGroupType<LANGUAGE_CODE>) => {
          if (group.links.length === 1) {
            const { href, title } = group.links[0];

            return {
              href,
              title,
            };
          }

          return {
            title: group.name,
            href: '#',
            children: group.links,
          };
        });
      },
      [content],
    );

    const links = useMemo(
      (): RecursiveLinksType[][] => {
        const myAccountLinksGroup = [];

        if (isLoggedIn) {
          if (hasMyAccountPermissions) {
            myAccountLinksGroup.push({
              href: urlCreator(ROUTES.myAccount),
              title: t('My Account'),
            });
          }

          if (hasAccessWishlistPermissions) {
            myAccountLinksGroup.push({
              href: urlCreator(ROUTES.myAccountWishlists),
              title: t('Wishlists'),
            });
          }

          return [myAccountLinksGroup, contentLinks];
        }

        return [contentLinks];
      },
      [
        hasMyAccountPermissions,
        hasAccessWishlistPermissions,
        contentLinks,
        isLoggedIn,
        urlCreator,
      ],
    );

    const handleModalLinkClick = (url: string): void => {
      openLink(url, push);
    };

    const injectedProps: InjectedProps = {
      isLoggedIn,
      languages: getAvailableLanguages().map((languageCode: LANGUAGE_CODE) => ({
        code: languageCode,
        label: LANGUAGE_LABELS[languageCode],
      })),
      activeLanguage,
      links,
      onLanguageChange: changeLanguage,
      onLogout: redirectToLogout,
      onModalLinkClick: handleModalLinkClick,
      onLinkClick,
      catalogLinks,
      catalog,
      companyName: profile?.debitor?.companyName,
      ownAssortment,
    };

    return <Component {...props} {...injectedProps} />;
  };

  return WithContainer;
};
